import { z } from "zod";
import { DateTimeWithOffset, NullishDateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { ProjectType } from "@/lib/domain/platform";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

const WorkerTag = z.object({
  id: z.number(),
  created_at: DateTimeWithOffset,
  // The model allows os | location | type | platform, but the /api-front/ contract points this field
  // at the file-category enum (video | image | …) through a schema name clash — don't narrow it from there.
  category: z.string(),
  value: z.string(),
});

const WorkerLocation = z.string().max(255);

const WorkerOut = z.object({
  id: z.number(),
  created_at: DateTimeWithOffset,
  worker_tags: WorkerTag.array().nullish(),
  type: ProjectType.default("android"),
  location: z.string(),
  global_worker: z.boolean(),
  name: z.string().default(""),
  commit_hash: z.string().nullish(),
  version: z.string().nullish(),
  client_name: z.string().nullish(),
  ip_address: z.string().nullish(),
  last_activity_time: NullishDateTimeWithOffset,
  last_seen_time: NullishDateTimeWithOffset,
  is_active: z.boolean().nullish(),
  status: z.enum(["running", "ready", "offline"]).default("offline"),
});

const WorkerBatchCaseOut = z.object({
  id: z.number(),
  case_id: z.number().nullish(),
  name: z.string().nullish(),
  state: z.enum([
    "unknown",
    "new",
    "waiting",
    "running",
    "success",
    "failure",
    "warning",
    "error",
    "skip",
    "anomalies_detected",
    "cancelled",
  ]),
  started_at: NullishDateTimeWithOffset,
  finished_at: NullishDateTimeWithOffset,
  is_current: z.boolean().default(false),
});

const WorkerBatchOut = z.object({
  id: z.number(),
  name: z.string().default(""),
  state: z.enum(["queued", "running", "completed", "cancelled"]),
  outcome: z
    .enum([
      "unknown",
      "new",
      "waiting",
      "running",
      "success",
      "failure",
      "warning",
      "error",
      "skip",
      "anomalies_detected",
      "cancelled",
    ])
    .nullish(),
  project_id: z.number(),
  project_name: z.string().nullish(),
  artifact_name: z.string().nullish(),
  schedule_time: DateTimeWithOffset,
  invocation_time: NullishDateTimeWithOffset,
  started_at: NullishDateTimeWithOffset,
  finished_at: NullishDateTimeWithOffset,
  cancelled_at: NullishDateTimeWithOffset,
  total_cases: z.number().default(0),
});

const WorkerCurrentBatchOut = WorkerBatchOut.extend({
  suite_name: z.string().nullish(),
  cases: WorkerBatchCaseOut.array().default([]),
  current_case_id: z.number().nullish(),
});

const WorkerRecentBatchOut = WorkerBatchOut.extend({
  time_taken: z.number().default(0),
});

const WorkerDetailOut = WorkerOut.extend({
  current_batch: WorkerCurrentBatchOut.nullish(),
  upcoming_batches: WorkerBatchOut.array().default([]),
  recent_batches: WorkerRecentBatchOut.array().default([]),
});

const WorkersListParameters = z.object({
  is_active: z.boolean().nullish(),
  type: ProjectType.nullish(),
  processing_type: z.enum(["api", "classic", "nextgen"]).nullish(),
  status: z.enum(["running", "ready", "offline"]).nullish(),
  search: z.string().nullish(),
  ordering: z.string().nullish(),
  limit: z.number().int().min(1).nullish(),
  offset: z.number().int().min(0).nullish(),
});

class WorkersEndpoint {
  public static readonly basePath = "workers";
  public static readonly schemas = {
    list: {
      parameters: WorkersListParameters,
      response: z.object({
        count: z.number(),
        items: WorkerOut.array(),
      }),
    },
    create: {
      body: z.object({
        name: z.string().min(1),
        type: ProjectType,
        location: WorkerLocation.optional(),
        ip_address: z.string().nullable().optional(),
        global_worker: z.boolean(),
      }),
      response: WorkerOut,
    },
    read: {
      parameters: z.object({
        upcoming_limit: z.number().int().min(0).max(20).nullish(),
        recent_limit: z.number().int().min(0).max(20).nullish(),
      }),
      response: WorkerDetailOut,
    },
    /** `type` and `location` left out stay as they are; `""` is what clears a location. */
    update: {
      body: z.object({
        name: z.string(),
        type: ProjectType.optional(),
        location: WorkerLocation.optional(),
        ip_address: z.string().nullable(),
        global_worker: z.boolean(),
      }),
    },
    regenerateKey: {
      response: z.object({
        id: z.number(),
        api_key: z.string(),
        /** What goes into the worker's config next to the key; differs per environment. */
        api_url: z.string(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public async list(
    query: z.input<typeof WorkersEndpoint.schemas.list.parameters> = {},
  ): ActionReturnType<z.infer<typeof WorkersEndpoint.schemas.list.response>> {
    const params = validateInput(WorkersEndpoint.schemas.list.parameters, query);
    if (!params.ok) return params;
    return request({
      log: ["Workers", "list"],
      method: "get",
      path: [WorkersEndpoint.basePath],
      query: params.data,
      response: WorkersEndpoint.schemas.list.response,
    });
  }

  public async create(
    body: z.infer<typeof WorkersEndpoint.schemas.create.body>,
  ): ActionReturnType<z.infer<typeof WorkersEndpoint.schemas.create.response>> {
    const bodyValidated = validateInput(WorkersEndpoint.schemas.create.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Workers", "create"],
      method: "post",
      path: [WorkersEndpoint.basePath],
      json: bodyValidated.data,
      response: WorkersEndpoint.schemas.create.response,
    });
  }

  /** The only response that ever carries the key, and the previous one stops working at once. */
  public regenerateKey(id: number): ActionReturnType<z.infer<typeof WorkersEndpoint.schemas.regenerateKey.response>> {
    return request({
      log: ["Workers", "regenerateKey"],
      method: "post",
      path: [WorkersEndpoint.basePath, id, "regenerate-key"],
      response: WorkersEndpoint.schemas.regenerateKey.response,
    });
  }

  public async read(
    id: number | string,
    query: z.input<typeof WorkersEndpoint.schemas.read.parameters> = {},
  ): ActionReturnType<z.infer<typeof WorkersEndpoint.schemas.read.response>> {
    const params = validateInput(WorkersEndpoint.schemas.read.parameters, query);
    if (!params.ok) return params;
    return request({
      log: ["Workers", "read"],
      method: "get",
      path: [WorkersEndpoint.basePath, id],
      query: params.data,
      response: WorkersEndpoint.schemas.read.response,
    });
  }

  public async update(
    id: string,
    body: z.infer<typeof WorkersEndpoint.schemas.update.body>,
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(WorkersEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Workers", "update"],
      method: "put",
      path: [WorkersEndpoint.basePath, id],
      json: bodyValidated.data,
    });
  }

  public delete(id: string): ActionReturnType<undefined> {
    return request({
      log: ["Workers", "delete"],
      method: "delete",
      path: [WorkersEndpoint.basePath, id],
    });
  }
}

export { WorkersEndpoint };
